import React, { HTMLAttributes } from 'react';
import { classNames } from '@/utils/classNames';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
  removable?: boolean;
  onRemove?: () => void;
  children: React.ReactNode;
}

const Badge: React.FC<BadgeProps> = ({
  variant = 'default',
  size = 'md',
  rounded = false,
  removable = false,
  onRemove,
  className,
  children,
  ...props
}) => {
  const baseClasses = 'inline-flex items-center font-medium transition-colors';

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-sm',
    lg: 'px-3 py-1.5 text-sm',
  };

  const variantClasses = {
    default: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-200',
    secondary: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    success: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    error: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    info: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  };

  const roundedClasses = rounded ? 'rounded-full' : 'rounded-md';

  const badgeClasses = classNames(
    baseClasses,
    sizeClasses[size],
    variantClasses[variant],
    roundedClasses,
    className
  );

  return (
    <span className={badgeClasses} {...props}>
      {children}
      {removable && onRemove && (
        <button
          type='button'
          onClick={onRemove}
          className={classNames(
            'ml-1.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full',
            'hover:bg-current/20 focus:bg-current/30',
            'focus:outline-none transition-colors touch-target'
          )}
          aria-label='Remove badge'
        >
          <svg className='h-2.5 w-2.5' fill='currentColor' viewBox='0 0 20 20'>
            <path
              fillRule='evenodd'
              d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
              clipRule='evenodd'
            />
          </svg>
        </button>
      )}
    </span>
  );
};

export default Badge;
